import React, { useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment, OrbitControls, useGLTF } from "@react-three/drei";
import * as THREE from "three";

// CAD/PBR model (normal rendering)
import cadUrl from "../../assets/parts/stanford_bunny_pbr.glb";

// FEM result (Von Mises) model
import vonMisesUrl from "../../assets/parts/standford_bunny_vonMises.gltf";


/* ---------- Helpers ---------- */
function makeWireframe(scene) {
  const wire = scene.clone(true);
  wire.traverse((o) => {
    if (!o.isMesh) return;
    o.geometry = o.geometry.clone();
    o.material = new THREE.MeshBasicMaterial({
      color: "#ffffff",
      wireframe: true,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
    });
  });
  return wire;
}

function centerSceneToOrigin(s) {
  const bbox = new THREE.Box3().setFromObject(s);
  const center = new THREE.Vector3();
  bbox.getCenter(center);
  s.position.sub(center);
  return bbox;
}

function getMaxAxisSize(bbox) {
  const size = new THREE.Vector3();
  bbox.getSize(size);
  return Math.max(size.x, size.y, size.z);
}



/* ---------- Model (CAD + FEM Von Mises) ---------- */
function Model({ progress, tilt, showMesh }) {
  const group = useRef();

  const { scene: cadSceneRaw } = useGLTF(cadUrl);
  const { scene: femSceneRaw } = useGLTF(vonMisesUrl);

  // clone once to avoid shared state
  const cadScene = useMemo(() => cadSceneRaw.clone(true), [cadSceneRaw]);
  const femScene = useMemo(() => femSceneRaw.clone(true), [femSceneRaw]);

  // normalize: center both, scale based on CAD size (constant; reveal does NOT change scale)
  const { normalizedScale, cadWire, femWire } = useMemo(() => {
    const cadBBox = centerSceneToOrigin(cadScene);
    centerSceneToOrigin(femScene);

    const cadMax = getMaxAxisSize(cadBBox);
    const TARGET = 1.5; // desired max size in world units
    const scale = cadMax > 0 ? TARGET / cadMax : 1;

    return {
      normalizedScale: scale,
      cadWire: makeWireframe(cadScene),
      femWire: makeWireframe(femScene),
    };
  }, [cadScene, femScene]);

  const mode = useMemo(() => {
    if (progress < 50) return "CAD";
    if (progress < 75) return "FEM";
    return "MACHINING";
  }, [progress]);

  // Opacity-only reveal in CAD mode
  const reveal = mode === "CAD" ? THREE.MathUtils.clamp(progress / 35, 0, 1) : 1;

  // allow opacity changes on CAD materials (do NOT touch FEM materials to preserve Von Mises)
  useEffect(() => {
    cadScene.traverse((o) => {
      if (!o.isMesh) return;
      o.material = o.material.clone();
      o.material.transparent = true;
      o.material.opacity = 1;
    });
  }, [cadScene]);

  useFrame(() => {
    if (!group.current) return;

    // CAD tilted, others fixed
    if (mode === "CAD") group.current.rotation.set(tilt[0], tilt[1], 0);
    else group.current.rotation.set(0, 0, 0);

    // CAD: fade only (NO scale change)
    cadScene.traverse((o) => {
      if (!o.isMesh) return;
      o.material.opacity = mode === "CAD" ? 0.15 + 0.85 * reveal : 1;
    });
  });

  const showCad = mode !== "FEM";
  const showFem = mode === "FEM";

  return (
    <group ref={group} scale={normalizedScale}>
      {/* CAD/PBR for CAD + CFD + MACHINING */}
      {showCad && <primitive object={cadScene} />}
      {showCad && showMesh && <primitive object={cadWire} />}

      {/* FEM Von Mises ONLY for FEM mode */}
      {showFem && (
  <group
    position={[0, 0, 0]}        // 👈 FEM only
    rotation={[0, 0, 0]}        // 👈 FEM only
    scale={500.0}                 // 👈 FEM only (relative to normalizedScale)
  >
    <primitive object={femScene} />
    {showMesh && <primitive object={femWire} />}
  </group>
)}
      {showFem && showMesh && <primitive object={femWire} />}


    </group>
  );
}

/* ---------- Viewport ---------- */
export default function CadViewport({
  progress,
  tilt = [0.22, -0.35, 0],
  showMesh = true,
  enableUserRotate = true,
}) {
  return (
    <div className="cad-viewport">
      <Canvas camera={{ position: [0, 0.3, 8], fov: 45 }}>
        <ambientLight intensity={0.7} />
        <directionalLight position={[3, 3, 3]} intensity={1.2} />
        <Environment preset="studio" />

        <Model progress={progress} tilt={tilt} showMesh={showMesh} />

        {enableUserRotate && (
          <OrbitControls
            enableRotate
            enableZoom
            enablePan={false}
            minDistance={3.5}
            maxDistance={4.5}
          />
        )}
      </Canvas>
    </div>
  );
}

useGLTF.preload(cadUrl);
useGLTF.preload(vonMisesUrl);

